import { Link } from 'react-router-dom';
import { ArrowRight, Rocket, Users, MapPin, Clock, Lightbulb, Building2, GraduationCap, Target, TrendingUp, Check, Zap, Code } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { SEO } from '../../components/SEO';

export default function LyonPage() {
  return (
    <main data-testid="seo-page-lyon" className="pt-32">
      <SEO 
        title="Création de site web à Lyon (69) | Webdesigner freelance Rhône, Villeurbanne"
        description="Webdesigner freelance pour startups et PME lyonnaises. Création de sites web à Lyon, Villeurbanne, Part-Dieu, Confluence. Étudiant entrepreneur (SNEE). Devis gratuit sous 48h."
        path="/creation-site-web-lyon"
      />

      {/* Hero Section */}
      <section className="container-main mb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/images/seo/lyon.png" 
                alt="Lyon" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-sm uppercase tracking-widest font-bold text-primary">
                Rhône • Métropole
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6">
              Création de site web
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-600 pb-2">
                à Lyon
              </span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
              Webdesigner freelance, j'accompagne les <strong>startups, PME et entrepreneurs</strong> de <strong>Lyon</strong> et <strong>Villeurbanne</strong>. Sites web modernes, performants et à budget maîtrisé.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white text-lg px-8 py-6">
                  Discuter de mon projet
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/tarifs">
                <Button variant="outline" className="text-lg px-8 py-6">
                  Voir les tarifs
                </Button>
              </Link>
            </div>
          </div>

          {/* Stats Card */}
          <div className="animate-fade-up animation-delay-200">
            <Card className="overflow-hidden border-2 bg-gradient-to-br from-red-600 to-rose-600 text-white">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6">La métropole de Lyon en chiffres</h2>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-3xl font-black">1,4 M</div>
                    <div className="text-sm opacity-80">habitants</div>
                  </div>
                  <div className="text-center p-4 bg-white/10 rounded-xl">
                    <div className="text-3xl font-black">59</div>
                    <div className="text-sm opacity-80">communes</div>
                  </div>
                  <div className="text-center p-4 bg-white/10 rounded-xl col-span-2">
                    <div className="text-xl font-bold">2ème métropole de France</div>
                    <div className="text-sm opacity-80 mt-1">
                      Pôle majeur de la Tech et de l'innovation
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: Étudiant Entrepreneur */}
      <section className="bg-muted/30 section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Photo startup */}
            <div className="order-2 lg:order-1">
              <img 
                src="/images/seo/lyon-office.jpg"
                alt="Création site web Lyon - Équipe startup en réunion"
                className="w-full h-auto rounded-2xl object-cover"
              />
              <p className="text-xs text-muted-foreground mt-2 text-right">© Freepik</p>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <GraduationCap className="h-4 w-4" />
                Statut National Étudiant-Entrepreneur (SNEE)
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Étudiant entrepreneur,<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-600">
                  je comprends vos enjeux
                </span>
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Je sais ce que c'est de lancer un projet avec un budget serré. De devoir faire des choix. 
                De vouloir un site pro sans y laisser toute sa trésorerie.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                C'est pour ça que je propose des <strong>tarifs adaptés aux startups et jeunes entreprises</strong>, 
                sans compromis sur la qualité. Parce que j'y suis passé aussi.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Zap className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">Agile</div>
                    <div className="text-sm text-muted-foreground">Itérations rapides</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Rocket className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">Livraison rapide</div>
                    <div className="text-sm text-muted-foreground">Délais tenus</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Lightbulb className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">Pragmatique</div>
                    <div className="text-sm text-muted-foreground">Solutions concrètes</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-white dark:bg-card rounded-xl border">
                  <Target className="h-8 w-8 text-red-600 shrink-0" />
                  <div>
                    <div className="font-bold">Focus ROI</div>
                    <div className="text-sm text-muted-foreground">Résultats mesurables</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Pour qui */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <span className="text-sm uppercase tracking-widest font-bold text-primary mb-4 block">
              Pour qui ?
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web pour les<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-600">
                entrepreneurs à Lyon
              </span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Startups, PME, indépendants à Lyon, Villeurbanne, Part-Dieu... Un site web qui convertit, pas juste une vitrine.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card: Startups */}
            <Card className="group hover:border-red-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Rocket className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Startups</h3>
                <p className="text-muted-foreground mb-4">
                  MVP, landing pages, applications web... Je vous aide à lancer vite et bien, sans exploser votre budget.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Landing page en 1 semaine
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Design moderne
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Optimisé conversion
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: PME */}
            <Card className="group hover:border-red-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">PME</h3>
                <p className="text-muted-foreground mb-4">
                  Site vitrine, e-commerce, refonte... Une présence en ligne professionnelle qui reflète votre expertise.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Site sur-mesure
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Référencement SEO
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Formation incluse
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Card: Indépendants */}
            <Card className="group hover:border-red-500/50 transition-all hover:shadow-lg border-2">
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform">
                  <Code className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Indépendants</h3>
                <p className="text-muted-foreground mb-4">
                  Consultants, freelances, coachs... Un site personnel qui vous démarque et génère des leads.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Personal branding
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Prise de RDV en ligne
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    Portfolio intégré
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section: Qualité métropole, budget maîtrisé */}
      <section className="bg-gradient-to-br from-red-600 to-rose-600 text-white section-padding">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm uppercase tracking-widest font-bold text-red-200 mb-4 block">
                Mon approche
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Qualité métropole,<br />
                budget maîtrisé
              </h2>
              <p className="text-lg text-red-100 mb-8 leading-relaxed">
                Les agences lyonnaises facturent cher. Très cher. Parce qu'elles ont des locaux Place Bellecour, 
                20 salariés et des frais de structure énormes.
              </p>
              <p className="text-lg text-red-100 mb-8 leading-relaxed">
                Moi, je suis freelance. Pas de bureaux luxueux, pas de commerciaux à payer. 
                Juste <strong>du travail de qualité au juste prix</strong>.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-white/10 rounded-xl">
                  <div className="text-3xl font-black">-40%</div>
                  <div className="text-sm opacity-80">vs agences</div>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-xl">
                  <div className="text-3xl font-black">1</div>
                  <div className="text-sm opacity-80">interlocuteur</div>
                </div>
                <div className="text-center p-4 bg-white/10 rounded-xl">
                  <div className="text-3xl font-black">48h</div>
                  <div className="text-sm opacity-80">réponse max</div>
                </div>
              </div>
            </div>

            {/* Photo Lyon */}
            <div>
              <img 
                src="/images/seo/lyon-view.jpg"
                alt="Création site web Lyon - Vue depuis Fourvière"
                className="w-full h-auto rounded-2xl object-cover"
              />
              <p className="text-xs text-red-200 mt-2 text-right">© Freepik</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section: Quartiers */}
      <section className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Création de site web dans toute la métropole de Lyon
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Lyon, Villeurbanne, Part-Dieu, Confluence, Gerland... Je me déplace pour vous rencontrer.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Presqu\'île', desc: 'Cœur de Lyon, commerces & services' },
              { name: 'Part-Dieu', desc: 'Quartier d\'affaires' },
              { name: 'Confluence', desc: 'Innovation & startups' },
              { name: 'Villeurbanne', desc: 'Tech & industrie' },
              { name: 'Vieux Lyon', desc: 'Tourisme & restauration' },
              { name: 'Gerland', desc: 'Biotech & recherche' },
            ].map((zone, index) => (
              <Card key={zone.name} className="hover:border-red-500/50 transition-colors">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-rose-600 flex items-center justify-center text-white font-bold shrink-0">
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-bold">{zone.name}</h3>
                    <p className="text-sm text-muted-foreground">{zone.desc}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="section-padding bg-muted/30">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Votre site web à Lyon
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Création de site internet à Lyon, Villeurbanne et dans toute la métropole. Devis gratuit sous 48h.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link to="/tarifs#contact">
                <Button className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white text-lg px-8 py-6 w-full sm:w-auto">
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/processus">
                <Button variant="outline" className="text-lg px-8 py-6 w-full sm:w-auto">
                  Découvrir mon processus
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Lyon (69)</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span>Devis sous 48h</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
